import { useState, type ReactNode } from "react";
import { appPath } from "../../api";
import { MarketingFooter, MarketingHeader, SectionLabel } from "./BlitzWeb";
import "../homepage.css";
import "./chapters.css";

/** The homepage chapter head: label, display heading at left, copy on the heading's cap line at right. */
export function SectionHead({ id, label, title, children, level = 2 }: { id?: string; label: ReactNode; title: ReactNode; children?: ReactNode; level?: 1 | 2 }) {
  const Heading = level === 1 ? "h1" : "h2";
  return <header className="chapter-head"><SectionLabel>{label}</SectionLabel><Heading id={id}>{title}</Heading>{children ? <div className="chapter-head-aside">{children}</div> : null}</header>;
}

export type ResourceId = "docs" | "pricing" | "changelog" | "acknowledgments";
export const RESOURCE_IDS: ResourceId[] = ["docs", "pricing", "changelog", "acknowledgments"];
type ResourceLink = (label: string, id: ResourceId) => ReactNode;

/** One footer for every public page. On the homepage `base` is empty and resources open in place. */
export function FooterNavigation({ base, resource }: { base: string; resource: ResourceLink }) {
  return <>
    <nav aria-label="Product"><h3>Product</h3><a href={appPath("/app")}>Synthesis Planner</a><a href={`${base}#molecule-lab`}>Molecule Lab <span>Soon</span></a><a href={`${base}#peptide-lab`}>Peptide Lab <span>Soon</span></a>{resource("Pricing", "pricing")}{resource("Changelog", "changelog")}</nav>
    <nav aria-label="Resources"><h3>Resources</h3><a href={`${base}#sample-route`}>Explore the route map</a>{resource("Quick-start docs", "docs")}<a href={`${base}#faq`}>FAQ</a></nav>
    <nav aria-label="Company"><h3>Company</h3><a href={`${base}#about`}>About</a><a href={appPath("/blog")}>Blog</a><a href={appPath("/careers")}>Careers</a><a href={`${base}#contact`}>Contact</a><a href={`${base}#contact`}>Early-access feedback</a></nav>
  </>;
}
export function FooterLegal({ resource }: { resource: ResourceLink }) {
  return <><a href={appPath("/privacy")}>Privacy</a><a href={appPath("/terms")}>Terms</a>{resource("Acknowledgments", "acknowledgments")}</>;
}

/** Blog, careers and BlitzLink pages share the homepage header and footer; anchors return to the homepage. */
export function SitePage({ mainId, overHero = false, className = "", children }: { mainId: string; overHero?: boolean; className?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const base = appPath("/");
  const resource: ResourceLink = (label, id) => <a href={`${base}#${id}`}>{label}</a>;
  return <div className={`synth-home site-page${overHero ? " site-page--hero" : ""} ${className}`.trim()}>
    <a className="home-skip" href={`#${mainId}`}>Skip to content</a>
    <MarketingHeader open={open} onToggle={() => setOpen(!open)} onClose={() => setOpen(false)} pricing={resource("Pricing", "pricing")} base={base} solid={!overHero} />
    <main id={mainId}>{children}</main>
    <MarketingFooter contactHref={`${base}#contact`} legal={<FooterLegal resource={resource} />}><FooterNavigation base={base} resource={resource} /></MarketingFooter>
  </div>;
}
